import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUrl';
import Header from '../components/Header';
import { getPasseiosLang } from '../utils/passeioLang';

function PasseiosList() {
  const { t, i18n } = useTranslation();
  const [passeios, setPasseios] = useState([]);
  const [passeiosFiltrados, setPasseiosFiltrados] = useState([]);
  const [passeiosExibidos, setPasseiosExibidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPasseios, setTotalPasseios] = useState(0);
  const [pagination, setPagination] = useState(null);
  const [hasFilters, setHasFilters] = useState(false);
  const [whatsappGlobal, setWhatsappGlobal] = useState('');
  const itemsPerPage = 12;
  
  const [filtros, setFiltros] = useState({
    localizacao: '',
    duracao: '',
    capacidade: '',
    disponivel: '',
    ordenarPor: 'nome'
  });

  const fetchPasseios = async (page = 1, usePagination = true) => {
    try {
      setLoading(true);
      let url = '/passeios';
      
      // Se usar paginação, adicionar parâmetros
      if (usePagination) {
        url += `?page=${page}&limit=${itemsPerPage}`;
      }
      
      const response = await api.get(url);
      
      // Verificar se a resposta tem paginação (novo formato)
      if (response.data && response.data.data && response.data.pagination) {
        // Formato com paginação do backend
        setPasseios(response.data.data);
        setPagination(response.data.pagination);
      } else if (Array.isArray(response.data)) {
        // Formato antigo (array direto) - para compatibilidade
        setPasseios(response.data);
        setPagination(null);
      } else {
        setPasseios([]);
        setPagination(null);
      }
    } catch (err) {
      console.error('Erro ao buscar passeios:', err);
      setPasseios([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Verificar busca na URL
    const params = new URLSearchParams(window.location.search);
    const busca = params.get('busca');
    if (busca) {
      setSearchTerm(busca);
    }
    fetchPasseios(1);
    fetchWhatsappGlobal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWhatsappGlobal = async () => {
    try {
      const response = await api.get('/configuracoes/whatsapp');
      setWhatsappGlobal(response.data.whatsapp || '');
    } catch (err) {
      console.error('Erro ao buscar WhatsApp global:', err);
    }
  };

  const handleWhatsAppClick = () => {
    if (!whatsappGlobal) {
      return;
    }

    const mensagem = 'Olá! Gostaria de adquirir ingressos para os passeios.';
    const whatsappUrl = `https://wa.me/${whatsappGlobal}?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Verificar se há filtros ativos e recarregar dados se necessário
  useEffect(() => {
    const hasActiveFilters = 
      searchTerm.trim() !== '' ||
      filtros.localizacao !== '' ||
      filtros.duracao !== '' ||
      filtros.capacidade !== '' ||
      filtros.disponivel !== '' ||
      filtros.ordenarPor !== 'nome';
    
    const prevHasFilters = hasFilters;
    setHasFilters(hasActiveFilters);
    
    // Se mudou o estado de filtros, recarregar
    if (prevHasFilters !== hasActiveFilters) {
      setCurrentPage(1);
      if (hasActiveFilters) {
        // Com filtros: carregar todos os passeios
        fetchPasseios(1, false);
      } else {
        // Sem filtros: usar paginação do backend
        fetchPasseios(1, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filtros.localizacao, filtros.duracao, filtros.capacidade, filtros.disponivel, filtros.ordenarPor]);

  useEffect(() => {
    const passeiosLang = getPasseiosLang(passeios, i18n.language);
    if (!passeiosLang.length) {
      setPasseiosFiltrados([]);
      setPasseiosExibidos([]);
      return;
    }
    
    let resultado = [...passeiosLang];

    // Busca por texto (nome, descrição ou localização)
    if (searchTerm.trim()) {
      const termo = searchTerm.toLowerCase();
      resultado = resultado.filter(passeio =>
        passeio.nome.toLowerCase().includes(termo) ||
        passeio.descricao?.toLowerCase().includes(termo) ||
        passeio.localizacao.toLowerCase().includes(termo)
      );
    }

    // Filtro por localização
    if (filtros.localizacao) {
      resultado = resultado.filter(passeio =>
        passeio.localizacao.toLowerCase().includes(filtros.localizacao.toLowerCase())
      );
    }

    // Filtro por duração
    if (filtros.duracao) {
      resultado = resultado.filter(passeio =>
        passeio.duracao && passeio.duracao.toLowerCase().includes(filtros.duracao.toLowerCase())
      );
    }

    // Filtro por capacidade
    if (filtros.capacidade) {
      const capacidade = parseInt(filtros.capacidade);
      resultado = resultado.filter(passeio => {
        const capMax = passeio.capacidade_maxima || 50;
        switch (capacidade) {
          case 10:
            return capMax <= 10;
          case 20:
            return capMax > 10 && capMax <= 20;
          case 30:
            return capMax > 20 && capMax <= 30;
          case 50:
            return capMax > 30 && capMax <= 50;
          case 100:
            return capMax > 50;
          default:
            return true;
        }
      });
    }

    // Filtro por disponibilidade
    if (filtros.disponivel !== '') {
      const disponivel = filtros.disponivel === 'true';
      resultado = resultado.filter(passeio => passeio.disponivel === disponivel);
    }

    // Ordenação
    resultado.sort((a, b) => {
      switch (filtros.ordenarPor) {
        case 'nome':
          return a.nome.localeCompare(b.nome);
        case 'duracao':
          return (a.duracao || '').localeCompare(b.duracao || '');
        case 'capacidade':
          return (a.capacidade_maxima || 0) - (b.capacidade_maxima || 0);
        default:
          return 0;
      }
    });

    setPasseiosFiltrados(resultado);
    
    // Calcular paginação do frontend se há filtros
    if (hasFilters) {
      const totalPagesFrontend = Math.ceil(resultado.length / itemsPerPage);
      setTotalPages(totalPagesFrontend || 1);
      setTotalPasseios(resultado.length);
    } else if (pagination) {
      // Sem filtros: usar paginação do backend
      setTotalPages(pagination.totalPages || 1);
      setTotalPasseios(pagination.total || 0);
    }
  }, [passeios, searchTerm, filtros, i18n.language, hasFilters, pagination]);

  // Calcular passeios exibidos baseado na página atual
  useEffect(() => {
    if (hasFilters) {
      // Com filtros: paginar no frontend
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setPasseiosExibidos(passeiosFiltrados.slice(startIndex, endIndex));
    } else {
      // Sem filtros: usar passeios diretamente (já vem paginado do backend)
      setPasseiosExibidos(passeiosFiltrados);
    }
  }, [passeiosFiltrados, currentPage, hasFilters]);

  const limparFiltros = () => {
    setSearchTerm('');
    setFiltros({
      localizacao: '',
      duracao: '',
      capacidade: '',
      disponivel: '',
      ordenarPor: 'nome'
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (!hasFilters) {
      // Sem filtros: buscar nova página do backend
      fetchPasseios(newPage, true);
    }
    // Scroll para o topo
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const passeiosLang = getPasseiosLang(passeios, i18n.language);
  const localizacoesUnicas = [...new Set(passeiosLang.map(p => p.localizacao).filter(Boolean))].sort();
  const duracoesUnicas = [...new Set(passeiosLang.map(p => p.duracao).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      <div className="pt-20">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-light text-stone-800 mb-6">
            {t('tours.title')}
          </h1>

          {/* Aviso sobre ingressos */}
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-8 rounded-r-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-orange-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm md:text-base text-orange-800">
                  <strong className="font-semibold">{t('tours.ticketsNotice.title')}</strong>
                  <span className="ml-1">{t('tours.ticketsNotice.message')}</span>
                  {whatsappGlobal && (
                    <button
                      onClick={handleWhatsAppClick}
                      className="ml-2 text-orange-600 hover:text-orange-800 font-semibold underline inline-flex items-center gap-1"
                    >
                      {t('tours.ticketsNotice.whatsapp')}
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Layout de duas colunas */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Coluna 1: Filtros (lateral esquerda) */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-md p-6 border border-stone-200 sticky top-24">
                <h2 className="text-xl font-semibold text-stone-800 mb-6">
                  {t('tours.filters')}
                </h2>

                {/* Barra de Busca */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('header.searchPlaceholder')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('header.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 pl-10 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    />
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Filtro Duração */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('tours.duration')}
                  </label>
                  <select
                    value={filtros.duracao}
                    onChange={(e) => setFiltros({ ...filtros, duracao: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="">{t('tours.all')}</option>
                    {duracoesUnicas.map((dur) => (
                      <option key={dur} value={dur}>
                        {dur}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filtro Capacidade */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('tours.capacity')}
                  </label>
                  <select
                    value={filtros.capacidade}
                    onChange={(e) => setFiltros({ ...filtros, capacidade: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="">{t('tours.all')}</option>
                    <option value="10">{t('tours.upTo')} 10 {t('tours.people')}</option>
                    <option value="20">11 - 20 {t('tours.people')}</option>
                    <option value="30">21 - 30 {t('tours.people')}</option>
                    <option value="50">31 - 50 {t('tours.people')}</option>
                    <option value="100">Mais de 50 {t('tours.people')}</option>
                  </select>
                </div>

                {/* Filtro Disponibilidade */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('tours.availability')}
                  </label>
                  <select
                    value={filtros.disponivel}
                    onChange={(e) => setFiltros({ ...filtros, disponivel: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="">{t('tours.all')}</option>
                    <option value="true">{t('tours.available')}</option>
                    <option value="false">{t('tours.unavailable')}</option>
                  </select>
                </div>

                {/* Filtro Localização */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('tours.location')}
                  </label>
                  <select
                    value={filtros.localizacao}
                    onChange={(e) => setFiltros({ ...filtros, localizacao: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="">{t('tours.all')}</option>
                    {localizacoesUnicas.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ordenação */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('tours.sortBy')}
                  </label>
                  <select
                    value={filtros.ordenarPor}
                    onChange={(e) => setFiltros({ ...filtros, ordenarPor: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="nome">{t('tours.name')}</option>
                    <option value="duracao">{t('tours.duration')}</option>
                    <option value="capacidade">{t('tours.capacity')}</option>
                  </select>
                </div>

                {/* Botão Limpar */}
                <button
                  onClick={limparFiltros}
                  className="w-full px-4 py-2 bg-stone-200 text-stone-800 rounded-lg font-medium hover:bg-stone-300 transition"
                >
                  {t('tours.clearFilters')}
                </button>
              </div>
            </div>

            {/* Coluna 2: Passeios (conteúdo principal) */}
            <div className="flex-1">
              {/* Resultado da Busca */}
              <div className="text-stone-600 mb-6 flex justify-between items-center">
                <div>
                  {totalPasseios > 0 ? (
                    <span>
                      {hasFilters 
                        ? `${passeiosFiltrados.length} ${passeiosFiltrados.length === 1 ? 'passeio encontrado' : 'passeios encontrados'}`
                        : `${totalPasseios} ${totalPasseios === 1 ? 'passeio encontrado' : 'passeios encontrados'}`
                      }
                      {hasFilters && passeiosFiltrados.length > 0 && (
                        <span className="text-stone-400 ml-2">
                          (Mostrando {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, passeiosFiltrados.length)})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-stone-500">{t('tours.noResults')}</span>
                  )}
                </div>
              </div>

              {/* Grid de Passeios */}
              {passeiosExibidos.length === 0 && !loading ? (
                <div className="text-center py-16 bg-white rounded-lg shadow-sm">
                  <div className="text-6xl mb-4">🔍</div>
                  <p className="text-xl text-stone-600 mb-2">
                    {t('tours.noResults')}
                  </p>
                  <p className="text-stone-500 mb-6">
                    Tente ajustar os filtros ou busca
                  </p>
                  <button
                    onClick={limparFiltros}
                    className="px-6 py-2 bg-stone-800 text-white rounded-lg font-medium hover:bg-stone-700 transition"
                  >
                    {t('tours.clearFilters')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {passeiosExibidos.map((passeio) => (
                    <Link
                      key={passeio.id}
                      to={`/passeio/${passeio.id}`}
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-stone-200 group"
                    >
                      <div className="relative h-56 overflow-hidden">
                        {passeio.Imagens && passeio.Imagens.length > 0 ? (
                          <img
                            src={getImageUrl(passeio.Imagens[0].imagem_url)}
                            alt={passeio.nome}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop&q=80';
                            }}
                          />
                        ) : passeio.imagem_url ? (
                          <img
                            src={getImageUrl(passeio.imagem_url)}
                            alt={passeio.nome}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop&q=80';
                            }}
                          />
                        ) : (
                          <img
                            src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop&q=80"
                            alt="Passeio turístico"
                            className="w-full h-full object-cover object-center"
                          />
                        )}
                        <div className="absolute top-3 right-3">
                          <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-medium">
                            {passeio.disponivel ? t('tours.available') : t('tours.unavailable')}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="text-xl font-semibold text-stone-900 mb-2 group-hover:text-orange-500 transition">
                          {passeio.nome}
                        </h3>
                        <p className="text-stone-600 mb-3 line-clamp-2 text-sm">
                          {passeio.descricao}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-stone-500">
                          <span>📍 {passeio.localizacao.length > 20 ? passeio.localizacao.substring(0, 20) + '...' : passeio.localizacao}</span>
                          {passeio.duracao && <span>⏱️ {passeio.duracao}</span>}
                          <span>👥 {t('tours.upTo')} {passeio.capacidade_maxima || 50} {t('tours.people')}</span>
                        </div>
                        <div className="flex items-center justify-end pt-3 border-t border-stone-200">
                          <span className="text-orange-500 font-medium text-sm group-hover:text-orange-600 transition">
                            {t('tours.viewDetails')} →
                          </span>
                        </div>
                      </div>
                    </Link>
                    ))}
                  </div>

                  {/* Controles de Paginação */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center items-center gap-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-white border border-stone-300 rounded-lg font-medium hover:bg-stone-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← Anterior
                      </button>
                      
                      <div className="flex gap-2">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-4 py-2 rounded-lg font-medium transition ${
                                currentPage === pageNum
                                  ? 'bg-stone-800 text-white'
                                  : 'bg-white border border-stone-300 hover:bg-stone-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 bg-white border border-stone-300 rounded-lg font-medium hover:bg-stone-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-stone-900 text-white py-10 mt-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <img
                  src="/logo-coop-igu.png"
                  alt="COOP-IGU - Turismo • Receptivo • Eventos"
                  className="h-10 md:h-12 w-auto mb-2 object-contain"
                />
                <p className="text-stone-400 text-sm mb-1">
                  {t('home.footer.tagline')}
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-3">{t('home.footer.links')}</h4>
                <ul className="space-y-1 text-stone-400 text-sm">
                  <li>
                    <Link to="/" className="hover:text-white transition">
                      {t('header.home')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/passeios" className="hover:text-white transition">
                      {t('header.tours')}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/#sobre" 
                      className="hover:text-white transition"
                    >
                      {t('header.about')}
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-3">{t('home.footer.socialMedia')}</h4>
                <a
                  href="https://www.instagram.com/coop_igu/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-stone-400 hover:text-pink-400 transition text-sm mb-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  @coop_igu
                </a>
                <div className="mt-4">
                  <h4 className="font-medium mb-3">{t('home.footer.location')}</h4>
                  <p className="text-stone-400 text-sm">
                    {t('home.footer.address')}
                  </p>
                  <p className="text-stone-400 text-sm">
                    {t('home.footer.city')}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-stone-800 pt-6 text-center text-stone-500 text-sm">
              <p>&copy; {new Date().getFullYear()} COOP-IGU. {t('home.footer.copyright')}</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default PasseiosList;

